/**
 * Reading what plays at a moment: a track's value, a shape between two outlines, a line between two
 * keys, and grids laid over ground. Whatever reads the map reads it here, so a map, a game master
 * and an editor all see the same moment.
 */
import { latOfMercY, mercY } from "./geometry";
import type { Extent } from "./map";
import type { Position } from "./primitives";
import {
  type Animated,
  type Camera,
  type CameraMove,
  type CameraSwitch,
  type Ease,
  isTrack,
  type Key,
  type ShapeGeometry,
  type ShapeMorph,
  type Shot,
} from "./things";

/* ── Reading tracks ─────────────────────────────────────────────────── */

/** Carries a moment of what plays onto the clock a track or a thing is keyed on. */
export type TimeMap = (t: number) => number;

/**
 * The moment on a track's or a thing's own clock. Tracks the timeline writes are keyed in ticks,
 * and while a scene plays, a time map carries the scene's seconds onto ticks.
 */
export const localTime = (value: object, t: number): number =>
  (value as { timeMap?: TimeMap }).timeMap?.(t) ?? t;

/** How a key is reached from the key before it: its own ease, or its track's. */
const easeInto = (key: object, ease: Ease): Ease => (key as { ease?: Ease }).ease ?? ease;

function segment(keys: { at: number }[], t: number): number {
  let lo = 0;
  let hi = keys.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (keys[mid].at <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** The slope at key `i` that never overshoots its neighbours. */
function slope(xs: (i: number) => number, ys: (i: number) => number, n: number, i: number) {
  const left = i > 0 ? (ys(i) - ys(i - 1)) / (xs(i) - xs(i - 1) || 1) : null;
  const right = i < n - 1 ? (ys(i + 1) - ys(i)) / (xs(i + 1) - xs(i) || 1) : null;
  if (left === null) return right ?? 0;
  if (right === null) return left;
  if (left * right <= 0) return 0;
  const m = (left + right) / 2;
  const cap = 3 * Math.min(Math.abs(left), Math.abs(right));
  return Math.sign(m) * Math.min(Math.abs(m), cap);
}

function hermite(y0: number, y1: number, m0: number, m1: number, h: number, u: number) {
  const u2 = u * u;
  const u3 = u2 * u;
  return (
    (2 * u3 - 3 * u2 + 1) * y0 +
    (u3 - 2 * u2 + u) * h * m0 +
    (-2 * u3 + 3 * u2) * y1 +
    (u3 - u2) * h * m1
  );
}

function sampleComponents(
  keys: Key<unknown>[],
  ease: Ease,
  t: number,
  width: number,
  read: (value: unknown, axis: number) => number,
  out: number[],
) {
  const n = keys.length;
  if (t <= keys[0].at || n === 1) {
    for (let a = 0; a < width; a++) out[a] = read(keys[0].value, a);
    return;
  }
  if (t >= keys[n - 1].at) {
    for (let a = 0; a < width; a++) out[a] = read(keys[n - 1].value, a);
    return;
  }
  const i = segment(keys, t);
  const k0 = keys[i];
  const k1 = keys[i + 1];
  const h = k1.at - k0.at;
  const u = h > 0 ? (t - k0.at) / h : 1;
  const into = easeInto(k1, ease);
  for (let a = 0; a < width; a++) {
    const y0 = read(k0.value, a);
    const y1 = read(k1.value, a);
    if (into === "step") out[a] = u < 1 ? y0 : y1;
    else if (into === "linear") out[a] = y0 + (y1 - y0) * u;
    else {
      const xs = (j: number) => keys[j].at;
      const ys = (j: number) => read(keys[j].value, a);
      out[a] = hermite(y0, y1, slope(xs, ys, n, i), slope(xs, ys, n, i + 1), h, u);
    }
  }
}

const scratch = [0, 0, 0, 0];

export function numberAt(value: Animated<number> | undefined, t: number, fallback: number) {
  if (value === undefined) return fallback;
  if (!isTrack(value)) return value;
  const at = localTime(value, t);
  sampleComponents(value.keys, value.ease ?? "linear", at, 1, v => v as number, scratch);
  return scratch[0];
}

/** How far along a keyed position's path each key lies, on the flat of the map. */
const runs = new WeakMap<Key<Position>[], number[]>();

function runOf(keys: Key<Position>[]): number[] {
  let run = runs.get(keys);
  if (!run) {
    run = [0];
    for (let i = 1; i < keys.length; i++) {
      const [lon0, lat0] = keys[i - 1].value;
      const [lon1, lat1] = keys[i].value;
      const crossing = keys[i].at === keys[i - 1].at;
      run.push(run[i - 1] + (crossing ? 0 : Math.hypot(lon1 - lon0, mercY(lat1) - mercY(lat0))));
    }
    runs.set(keys, run);
  }
  return run;
}

/**
 * A position at `t`. Keyed, its keys are its path, each reached at its `at` along a straight line
 * on the map from the one before; its ease is only how fast it goes along that path. Two keys at
 * the same moment, at longitude 180 and -180, cross the antimeridian: no way at all, so `smooth`
 * eases each side of it on its own.
 */
export function positionAt(value: Animated<Position>, t: number): Position {
  if (!isTrack(value)) return value;
  const { keys } = value;
  const n = keys.length;
  const at = localTime(value, t);
  if (at <= keys[0].at || n === 1) return keys[0].value;
  if (at >= keys[n - 1].at) return keys[n - 1].value;
  const i = segment(keys, at);
  const [from, to] = [keys[i], keys[i + 1]];
  const into = easeInto(to, value.ease ?? "linear");
  if (into === "step") return from.value;
  const h = to.at - from.at;
  let u = h > 0 ? (at - from.at) / h : 1;
  if (into === "smooth") {
    const run = runOf(keys);
    const leg = run[i + 1] - run[i];
    if (leg > 0) {
      let first = i;
      while (first > 0 && keys[first - 1].at !== keys[first].at) first--;
      let last = i + 1;
      while (last < n - 1 && keys[last + 1].at !== keys[last].at) last++;
      const xs = (j: number) => keys[first + j].at;
      const ys = (j: number) => run[first + j];
      const count = last - first + 1;
      const m0 = slope(xs, ys, count, i - first);
      const m1 = slope(xs, ys, count, i + 1 - first);
      u = (hermite(run[i], run[i + 1], m0, m1, h, u) - run[i]) / leg;
    }
  }
  const y = mercY(from.value[1]);
  return [
    from.value[0] + (to.value[0] - from.value[0]) * u,
    latOfMercY(y + (mercY(to.value[1]) - y) * u),
  ];
}

/**
 * The leg of a keyed position's path it goes along at `t`, as its two ends: the one it is on, or
 * the last it went along while it stands still, or the first it will before it sets off. Held, or
 * never moving, it has none.
 */
export function legAt(value: Animated<Position>, t: number): [Position, Position] | null {
  if (!isTrack(value)) return null;
  const { keys } = value;
  const moves = (j: number) =>
    keys[j].at !== keys[j + 1].at &&
    (keys[j].value[0] !== keys[j + 1].value[0] || keys[j].value[1] !== keys[j + 1].value[1]);
  const leg = (j: number): [Position, Position] => [keys[j].value, keys[j + 1].value];
  const on = Math.min(segment(keys, localTime(value, t)), keys.length - 2);
  for (let j = on; j >= 0; j--) if (moves(j)) return leg(j);
  for (let j = on + 1; j < keys.length - 1; j++) if (moves(j)) return leg(j);
  return null;
}

/** Easing out of a start and into an end, as OpenLayers' views do. */
const inAndOut = (u: number) => u * u * (3 - 2 * u);

export function rangeAt(
  value: Animated<[number, number]> | undefined,
  t: number,
): [number, number] {
  if (value === undefined) return [0, 1];
  if (!isTrack(value)) return value;
  const at = localTime(value, t);
  sampleComponents(
    value.keys,
    value.ease ?? "linear",
    at,
    2,
    (v, a) => (v as Position)[a],
    scratch,
  );
  return [scratch[0], scratch[1]];
}

export function boxAt(
  value: Animated<[number, number, number, number]>,
  t: number,
): [number, number, number, number] {
  if (!isTrack(value)) return value;
  const at = localTime(value, t);
  sampleComponents(
    value.keys,
    value.ease ?? "smooth",
    at,
    4,
    (v, a) => (v as number[])[a],
    scratch,
  );
  return [scratch[0], scratch[1], scratch[2], scratch[3]];
}

export function stepAt<T>(value: Animated<T>, t: number): T {
  if (!isTrack(value)) return value;
  return value.keys[segment(value.keys, localTime(value, t))].value;
}

const rgbCache = new Map<string, [number, number, number]>();
export function rgbOf(hex: string): [number, number, number] {
  const known = rgbCache.get(hex);
  if (known) return known;
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map(c => c + c)
          .join("")
      : clean;
  const rgb: [number, number, number] = [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
  rgbCache.set(hex, rgb);
  return rgb;
}

const hex2 = (n: number) =>
  Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, "0");

const alphaOf = (hex: string) => {
  const clean = hex.replace("#", "");
  return clean.length === 8 ? Number.parseInt(clean.slice(6, 8), 16) : 255;
};

export function colorAt(value: Animated<string>, t: number): string {
  if (!isTrack(value)) return value;
  const at = localTime(value, t);
  const translucent = value.keys.some(key => alphaOf(key.value) < 255);
  sampleComponents(
    value.keys,
    value.ease === "step" ? "step" : "linear",
    at,
    translucent ? 4 : 3,
    (v, a) => (a < 3 ? rgbOf(v as string)[a] : alphaOf(v as string)),
    scratch,
  );
  const rgb = `#${hex2(scratch[0])}${hex2(scratch[1])}${hex2(scratch[2])}`;
  return translucent ? `${rgb}${hex2(scratch[3])}` : rgb;
}

/* ── Shapes that change shape ───────────────────────────────────────── */

const RESAMPLED = 96;
const MOST_RESAMPLED = 512;
/** Cells across the grid ground is passed between keys on. */
const ADVANCE_CELLS = 240;

/**
 * A line with every point of its own kept and more set evenly along its segments by their length,
 * so it has about `count` in all.
 */
function densify(points: Position[], count: number): Position[] {
  const lengths: number[] = [];
  let total = 0;
  for (let i = 0; i + 1 < points.length; i++) {
    const length = Math.hypot(points[i + 1][0] - points[i][0], points[i + 1][1] - points[i][1]);
    lengths.push(length);
    total += length;
  }
  const extra = Math.max(0, count - points.length);
  const out: Position[] = [];
  for (let i = 0; i + 1 < points.length; i++) {
    const [ax, ay] = points[i];
    const [bx, by] = points[i + 1];
    out.push([ax, ay]);
    const more = total > 0 ? Math.round((extra * lengths[i]) / total) : 0;
    for (let k = 1; k <= more; k++) {
      const u = k / (more + 1);
      out.push([ax + (bx - ax) * u, ay + (by - ay) * u]);
    }
  }
  if (points.length) out.push(points[points.length - 1] as Position);
  return out.length ? out : [...points];
}

/** Twice a ring's area, above 0 when it runs anticlockwise. */
function signedArea(ring: Position[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    sum += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return sum;
}

function inRing(x: number, y: number, ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * A pair of keys worked out once. Spreading, both keys are measured on a grid as distances to their
 * edges, and the ground between them changes hands across the time between them, as `spreadSteps`
 * says. A shape whose one outline holds still while its holes change, such as fog with ground
 * explored in it, spreads its holes alone, on a grid fitted to them, so they move as finely as a
 * shape their size would.
 */
type Morph =
  | Spread
  | { kind: "holes"; outline: Position[]; holes: Spread; at: number; shape: Position[][][] };

interface Spread {
  kind: "spread";
  grid: BlendGrid;
  /**
   * The cells that change hands between the keys, the only ones that change: those only the second
   * key holds, each with how far it lies from the first and how far inside the second's edge, and
   * those only the first holds, with how far each lies from the second and inside the first's edge.
   */
  gainedCells: Int32Array;
  gainedFar: Float32Array;
  gainedDepth: Float32Array;
  lostCells: Int32Array;
  lostFar: Float32Array;
  lostDepth: Float32Array;
  nearestGained: number;
  farthestGained: number;
  nearestLost: number;
  farthestLost: number;
  /** Where ground stands between the keys as last read: above 0 wherever it is held. */
  between: Float32Array;
  /** The squares the outline crossed when last traced, in order; none before the first tracing. */
  crossed: Int32Array | null;
  crossedCount: number;
  at: number;
  shape: Position[][][];
}

/** Square cells on the flat map; `south` and `cell` are in degrees of Mercator. */
interface BlendGrid {
  west: number;
  south: number;
  cell: number;
  cols: number;
  rows: number;
}

type Origin = Position[][];

/** What a key without an origin is measured from, as a key to the morphs worked out. */
const FROM_THE_GROUND: Origin = [];

const morphs: Record<
  ShapeMorph,
  WeakMap<Position[][][], WeakMap<Position[][][], WeakMap<Origin, Morph>>>
> = {
  spread: new WeakMap(),
};

/** The morphs worked out between two keys, by how they morph, keyed by the first key and the second. */
function morphsBetween(
  from: Position[][][],
  to: Position[][][],
  how: ShapeMorph,
): WeakMap<Origin, Morph> {
  let byTo = morphs[how].get(from);
  if (!byTo) {
    byTo = new WeakMap();
    morphs[how].set(from, byTo);
  }
  let byOrigin = byTo.get(to);
  if (!byOrigin) {
    byOrigin = new WeakMap();
    byTo.set(to, byOrigin);
  }
  return byOrigin;
}

function morphOf(
  from: Position[][][],
  to: Position[][][],
  how: ShapeMorph,
  origin: Origin = FROM_THE_GROUND,
): Morph {
  const byOrigin = morphsBetween(from, to, how);
  let morph = byOrigin.get(origin);
  if (!morph) {
    const steps = takeBegun(from, to, origin) ?? morphSteps(from, to, origin);
    let step = steps.next();
    while (!step.done) step = steps.next();
    morph = step.value;
    byOrigin.set(origin, morph);
  }
  keepMorph(morph, byOrigin, origin);
  return morph;
}

/** The most bytes the grids of the morphs kept between reads may take. */
const MORPHS_KEPT_MOST_BYTES = 32 * 1024 * 1024;

/**
 * The morphs kept, least lately read first, each with the bytes its grids take and where it is
 * kept. Past the most bytes, the least lately read are let go, to be worked out again if read.
 */
const keptMorphs = new Map<
  Morph,
  { bytes: number; byOrigin: WeakMap<Origin, Morph>; origin: Origin }
>();
let keptMorphBytes = 0;

/** The bytes a morph's grids take. */
function bytesOf(morph: Morph): number {
  const spread = morph.kind === "holes" ? morph.holes : morph;
  return (
    spread.between.byteLength +
    3 * (spread.gainedCells.byteLength + spread.lostCells.byteLength) +
    (spread.crossed?.byteLength ?? 0)
  );
}

/** Keeps a morph as the one most lately read, letting go of the least lately read past the most bytes. */
function keepMorph(morph: Morph, byOrigin: WeakMap<Origin, Morph>, origin: Origin) {
  const kept = keptMorphs.get(morph) ?? { bytes: 0, byOrigin, origin };
  keptMorphs.delete(morph);
  keptMorphBytes -= kept.bytes;
  kept.bytes = bytesOf(morph);
  keptMorphs.set(morph, kept);
  keptMorphBytes += kept.bytes;
  for (const oldest of keptMorphs.keys()) {
    if (keptMorphBytes <= MORPHS_KEPT_MOST_BYTES || oldest === morph) break;
    letGoOf(oldest);
  }
}

/** Stops keeping a morph, so it is worked out again if read. */
function letGoOf(morph: Morph) {
  const kept = keptMorphs.get(morph);
  if (!kept) return;
  keptMorphs.delete(morph);
  keptMorphBytes -= kept.bytes;
  if (kept.byOrigin.get(kept.origin) === morph) kept.byOrigin.delete(kept.origin);
}

/** The most morphs begun ahead and not finished at once; past it, the least lately stepped are dropped. */
const MORPHS_BEGUN_MOST = 16;

/** Morphs begun ahead and not finished, least lately stepped first. */
const begun: {
  from: Position[][][];
  to: Position[][][];
  origin: Origin;
  steps: Generator<void, Morph>;
}[] = [];

/** The steps of the morph begun ahead between two keys, no longer kept as begun; none if not begun. */
function takeBegun(from: Position[][][], to: Position[][][], origin: Origin) {
  const k = begun.findIndex(b => b.from === from && b.to === to && b.origin === origin);
  if (k < 0) return undefined;
  const [taken] = begun.splice(k, 1);
  return taken?.steps;
}

/** The steps working out the morph between two keys, each about as long as measuring one key. */
function* morphSteps(from: Position[][][], to: Position[][][], origin: Origin) {
  const at = origin.length ? origin : undefined;
  const holes = holesOf(from, to);
  if (holes) {
    return {
      kind: "holes",
      outline: holes.outline,
      holes: yield* spreadSteps(holes.was, holes.will, at),
      at: -1,
      shape: [],
    } satisfies Morph as Morph;
  }
  return (yield* spreadSteps(from, to, at)) as Morph;
}

/** Whether the morph between two keys is worked out already. */
function hasMorph(
  from: Position[][][],
  to: Position[][][],
  how: ShapeMorph,
  origin: Origin = FROM_THE_GROUND,
): boolean {
  return !!morphs[how].get(from)?.get(to)?.has(origin);
}

/**
 * Takes the next step toward the first morph a shape goes through between `from` and `to` that is
 * not worked out yet, so a caller with a few milliseconds to spare can have it ready before it is
 * drawn; whether there was a step to take.
 */
export function prepareMorph(value: ShapeGeometry, from: number, to: number): boolean {
  if (Array.isArray(value)) return false;
  const { keys } = value;
  const start = localTime(value, from);
  const end = localTime(value, to);
  for (let i = segment(keys, start); i + 1 < keys.length && keys[i].at <= end; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (b.at <= a.at || easeInto(b, value.ease ?? "linear") === "step") continue;
    const how = b.morph ?? "spread";
    const given = originOf(keys, i);
    if (hasMorph(a.value, b.value, how, given)) continue;
    const origin = given ?? FROM_THE_GROUND;
    const steps = takeBegun(a.value, b.value, origin) ?? morphSteps(a.value, b.value, origin);
    const step = steps.next();
    if (step.done) {
      const byOrigin = morphsBetween(a.value, b.value, how);
      byOrigin.set(origin, step.value);
      keepMorph(step.value, byOrigin, origin);
    } else {
      begun.push({ from: a.value, to: b.value, origin, steps });
      if (begun.length > MORPHS_BEGUN_MOST) begun.shift();
    }
    return true;
  }
  return false;
}

type ShapeKeys = Exclude<ShapeGeometry, Position[][][]>["keys"];

/** The share of a shape's outline past which the stretches that stood still say nothing of a way. */
const STILL_MOST = 0.8;

const stillOrigins = new WeakMap<ShapeKeys, (Origin | null)[]>();

/**
 * Where the change into key `i + 1` starts: its own origin, or, for a key with none that takes the
 * shape to nothing or brings it from nothing, the stretches of its outline that stood still across
 * its keys, as ground given up falls back to the line it set out from and ground taken comes out of
 * the line it holds. None where nothing stood still, or nearly everything did.
 */
function originOf(keys: ShapeKeys, i: number): Origin | undefined {
  const a = keys[i] as ShapeKeys[number];
  const b = keys[i + 1] as ShapeKeys[number];
  if (b.origin) return b.origin;
  const toNothing = b.value.length === 0 && a.value.length > 0;
  const fromNothing = a.value.length === 0 && b.value.length > 0;
  if (!toNothing && !fromNothing) return undefined;
  let known = stillOrigins.get(keys);
  if (!known) {
    known = [];
    stillOrigins.set(keys, known);
  }
  let found = known[i];
  if (found === undefined) {
    if (toNothing) {
      const first = keys.slice(0, i + 1).find(key => key.value.length > 0) as ShapeKeys[number];
      found = stillEdges(first.value, a.value);
    } else {
      const last = keys
        .slice(i + 1)
        .reverse()
        .find(key => key.value.length > 0) as ShapeKeys[number];
      found = stillEdges(last.value, b.value);
    }
    known[i] = found;
  }
  return found ?? undefined;
}

/** A position as words, to the centimetre, so the same corner in two keys is the same. */
const cornerKey = (p: Position) => `${Math.round(p[0] * 1e7)},${Math.round(p[1] * 1e7)}`;
const sideKey = (p: Position, q: Position) => {
  const a = cornerKey(p);
  const b = cornerKey(q);
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

/** How many corners a ring has, its first not given again at its end. */
const cornersOf = (ring: Position[]) =>
  ring.length > 1 &&
  ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
  ring[0]?.[1] === ring[ring.length - 1]?.[1]
    ? ring.length - 1
    : ring.length;

/**
 * The stretches of `own`'s outline that `other`'s outline has too, side for side, as lines; none
 * where there are none or they are nearly all of it.
 */
function stillEdges(other: Position[][][], own: Position[][][]): Origin | null {
  if (other === own) return null;
  const theirs = new Set<string>();
  for (const polygon of other) {
    for (const ring of polygon) {
      const n = cornersOf(ring);
      for (let k = 0; k < n; k++) {
        theirs.add(sideKey(ring[k] as Position, ring[(k + 1) % n] as Position));
      }
    }
  }
  const lines: Position[][] = [];
  let still = 0;
  let total = 0;
  for (const polygon of own) {
    for (const ring of polygon) {
      const n = cornersOf(ring);
      const runs: Position[][] = [];
      let run: Position[] | null = null;
      let firstStill = false;
      for (let k = 0; k < n; k++) {
        const p = ring[k] as Position;
        const q = ring[(k + 1) % n] as Position;
        const length = Math.hypot(q[0] - p[0], q[1] - p[1]);
        total += length;
        if (!theirs.has(sideKey(p, q))) {
          run = null;
          continue;
        }
        still += length;
        if (k === 0) firstStill = true;
        if (!run) {
          run = [p];
          runs.push(run);
        }
        run.push(q);
      }
      if (firstStill && run && runs.length > 1 && run !== runs[0]) {
        const head = runs.shift() as Position[];
        run.push(...head.slice(1));
      }
      lines.push(...runs);
    }
  }
  return lines.length && still <= STILL_MOST * total ? lines : null;
}

/** The outline two keys share and the holes each has in it, when they differ only in their holes. */
function holesOf(from: Position[][][], to: Position[][][]) {
  const [a] = from;
  const [b] = to;
  if (from.length !== 1 || to.length !== 1 || !a || !b || a.length < 2 || b.length < 2) return null;
  const [outline, ...was] = a as [Position[], ...Position[][]];
  const [next, ...will] = b as [Position[], ...Position[][]];
  const still =
    outline.length === next.length &&
    outline.every(([x, y], k) => x === next[k]?.[0] && y === next[k]?.[1]);
  if (!still) return null;
  return { outline, was: was.map(ring => [ring]), will: will.map(ring => [ring]) };
}

/**
 * Two runs of points paired in order along both at once, at the least total distance between
 * partners: first with first, last with last, every point given at least one partner, and neither
 * run ever walked backwards. Longitude is measured shrunk by latitude, as distance on the ground.
 */
function warp(a: Position[], b: Position[]): { from: Position[]; to: Position[]; cost: number } {
  const n = a.length;
  const m = b.length;
  let lat = 0;
  for (const p of a) lat += p[1];
  for (const p of b) lat += p[1];
  const shrink = Math.cos(((lat / (n + m)) * Math.PI) / 180);
  const total = new Float64Array(n * m);
  for (let i = 0; i < n; i++) {
    const p = a[i] as Position;
    for (let j = 0; j < m; j++) {
      const q = b[j] as Position;
      const here = Math.hypot((p[0] - q[0]) * shrink, p[1] - q[1]);
      let before = Number.POSITIVE_INFINITY;
      if (i > 0) before = Math.min(before, total[(i - 1) * m + j]);
      if (j > 0) before = Math.min(before, total[i * m + j - 1]);
      if (i > 0 && j > 0) before = Math.min(before, total[(i - 1) * m + j - 1]);
      total[i * m + j] = here + (i === 0 && j === 0 ? 0 : before);
    }
  }
  const from: Position[] = [];
  const to: Position[] = [];
  let i = n - 1;
  let j = m - 1;
  for (;;) {
    from.push(a[i] as Position);
    to.push(b[j] as Position);
    if (i === 0 && j === 0) break;
    if (i === 0) j--;
    else if (j === 0) i--;
    else {
      const both = total[(i - 1) * m + j - 1];
      const first = total[(i - 1) * m + j];
      const second = total[i * m + j - 1];
      if (both <= first && both <= second) {
        i--;
        j--;
      } else if (first <= second) i--;
      else j--;
    }
  }
  from.reverse();
  to.reverse();
  return { from, to, cost: total[n * m - 1] };
}

/**
 * Ground passing between two keys as a front moves. Ground both keys hold stays held, and their
 * shared edges stay where they are. Ground only the second holds is taken in order of its distance
 * from the first's, nearest first, so the edge advances parallel to where it began; ground only
 * the first holds is given up farthest from the second's first, so the edge falls back toward it.
 * Both begin at once, whatever lies between the keys, and finish together at the second key. A
 * small first key is where new ground spreads from. With an origin, both are measured from it
 * instead, as a front's arrival time is from where it set out.
 */
function* spreadSteps(
  from: Position[][][],
  to: Position[][][],
  origin?: Origin,
): Generator<void, Spread> {
  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  for (const polygons of [from, to]) {
    for (const polygon of polygons) {
      for (const [lon, lat] of polygon[0] ?? []) {
        const y = mercY(lat);
        west = Math.min(west, lon);
        east = Math.max(east, lon);
        south = Math.min(south, y);
        north = Math.max(north, y);
      }
    }
  }
  if (west > east) west = east = south = north = 0;
  const cell = Math.max(east - west, north - south, 1e-9) / ADVANCE_CELLS;
  const grid: BlendGrid = {
    west: west - 2 * cell,
    south: south - 2 * cell,
    cell,
    cols: Math.ceil((east - west) / cell) + 5,
    rows: Math.ceil((north - south) / cell) + 5,
  };
  const size = grid.cols * grid.rows;
  const ownFrom = from.length ? distances(from, grid) : null;
  yield;
  const ownTo = to.length ? distances(to, grid) : null;
  yield;
  const fromOrigin = origin ? distancesFrom(origin, grid) : null;
  if (origin) yield;
  const nothing = () => new Float32Array(size).fill(Number.NEGATIVE_INFINITY);
  const inFrom = ownFrom ?? (ownTo && !fromOrigin ? fromInnermost(ownTo, grid) : nothing());
  const inTo = ownTo ?? (ownFrom && !fromOrigin ? fromInnermost(ownFrom, grid) : nothing());
  let nearestGained = Number.POSITIVE_INFINITY;
  let farthestGained = 0;
  let nearestLost = Number.POSITIVE_INFINITY;
  let farthestLost = 0;
  const between = new Float32Array(size);
  let gainedCount = 0;
  let lostCount = 0;
  for (let k = 0; k < size; k++) {
    const a = inFrom[k] as number;
    const b = inTo[k] as number;
    if (b > 0 && a <= 0) {
      const far = fromOrigin ? (fromOrigin[k] as number) : -a;
      nearestGained = Math.min(nearestGained, far);
      farthestGained = Math.max(farthestGained, far);
    } else if (a > 0 && b <= 0) {
      const far = fromOrigin ? (fromOrigin[k] as number) : -b;
      nearestLost = Math.min(nearestLost, far);
      farthestLost = Math.max(farthestLost, far);
    }
    if (a > 0 && b > 0) between[k] = Math.min(a, b);
    else if (b > 0) gainedCount++;
    else if (a > 0) lostCount++;
    else between[k] = Math.max(a, b);
  }
  const gainedCells = new Int32Array(gainedCount);
  const gainedFar = new Float32Array(gainedCount);
  const gainedDepth = new Float32Array(gainedCount);
  const lostCells = new Int32Array(lostCount);
  const lostFar = new Float32Array(lostCount);
  const lostDepth = new Float32Array(lostCount);
  for (let k = 0, g = 0, l = 0; k < size; k++) {
    const a = inFrom[k] as number;
    const b = inTo[k] as number;
    if (a > 0 && b > 0) continue;
    if (b > 0) {
      gainedCells[g] = k;
      gainedFar[g] = a <= 0 ? (fromOrigin ? (fromOrigin[k] as number) : -a) : -1;
      gainedDepth[g++] = b;
    } else if (a > 0) {
      lostCells[l] = k;
      lostFar[l] = b <= 0 ? (fromOrigin ? (fromOrigin[k] as number) : -b) : -1;
      lostDepth[l++] = a;
    }
  }
  return {
    kind: "spread",
    grid,
    gainedCells,
    gainedFar,
    gainedDepth,
    lostCells,
    lostFar,
    lostDepth,
    nearestGained: Number.isFinite(nearestGained) ? nearestGained : 0,
    farthestGained,
    nearestLost: Number.isFinite(nearestLost) ? nearestLost : 0,
    farthestLost,
    between,
    crossed: null,
    crossedCount: 0,
    at: -1,
    shape: [],
  };
}

/**
 * Distances inside ground measured from each piece's innermost cell instead of its edge: 0 there and
 * below 0 everywhere else, so ground spreading from them grows out of each piece's middle.
 */
function fromInnermost(inside: Float32Array, { cols }: BlendGrid): Float32Array {
  const piece = new Int32Array(inside.length).fill(-1);
  const deepest: number[] = [];
  const stack: number[] = [];
  let overall = 0;
  for (let start = 0; start < inside.length; start++) {
    if (!(inside[start] > 0) || piece[start] >= 0) continue;
    const id = deepest.length;
    let most = 0;
    piece[start] = id;
    stack.push(start);
    while (stack.length) {
      const k = stack.pop() as number;
      most = Math.max(most, inside[k] as number);
      const c = k % cols;
      for (const next of [c > 0 ? k - 1 : -1, c < cols - 1 ? k + 1 : -1, k - cols, k + cols]) {
        if (next >= 0 && next < inside.length && inside[next] > 0 && piece[next] < 0) {
          piece[next] = id;
          stack.push(next);
        }
      }
    }
    deepest.push(most);
    overall = Math.max(overall, most);
  }
  return inside.map(
    (value, k) => value - (piece[k] >= 0 ? (deepest[piece[k]] as number) : overall),
  );
}

/** The cells that came to be held, or stopped being held, at the last move between keys. */
let flipped = new Int32Array(256);
let flippedCount = 0;

/**
 * Moves the ground between a spread's keys to `u` of the way, in `between`: only the cells that
 * change hands are worked out again. Each that comes to be held, or stops being held, goes in
 * `flipped`.
 */
function moveBetween(morph: Spread, u: number) {
  const { between, gainedCells, gainedFar, gainedDepth, lostCells, lostFar, lostDepth } = morph;
  const reached = morph.nearestGained + u * (morph.farthestGained - morph.nearestGained);
  const kept = morph.nearestLost + (1 - u) * (morph.farthestLost - morph.nearestLost);
  if (flipped.length < gainedCells.length + lostCells.length) {
    flipped = new Int32Array(gainedCells.length + lostCells.length);
  }
  flippedCount = 0;
  for (let n = 0; n < gainedCells.length; n++) {
    const k = gainedCells[n];
    const was = between[k] > 0;
    between[k] = Math.min(reached - gainedFar[n], gainedDepth[n]);
    if (between[k] > 0 !== was) flipped[flippedCount++] = k;
  }
  for (let n = 0; n < lostCells.length; n++) {
    const k = lostCells[n];
    const was = between[k] > 0;
    between[k] = Math.min(kept - lostFar[n], lostDepth[n]);
    if (between[k] > 0 !== was) flipped[flippedCount++] = k;
  }
}

/** Each cell's distance to the polygons' edge, in cells: positive inside them, negative outside. */
function distances(polygons: Position[][][], grid: BlendGrid): Float32Array {
  const { cols, rows, cell } = grid;
  const size = cols * rows;
  if (insideScratch.length < size) insideScratch = new Uint8Array(size);
  const inside = insideScratch;
  inside.fill(0, 0, size);
  const near = new Float32Array(size).fill(Number.POSITIVE_INFINITY);
  const crossings: number[][] = Array.from({ length: rows }, () => []);
  for (const polygon of polygons) {
    for (const row of crossings) row.length = 0;
    for (const ring of polygon) {
      const n = ring.length;
      if (ringCols.length < n) {
        ringCols = new Float64Array(n * 2);
        ringRows = new Float64Array(n * 2);
      }
      for (let i = 0; i < n; i++) {
        const point = ring[i] as Position;
        ringCols[i] = (point[0] - grid.west) / cell - 0.5;
        ringRows[i] = (mercY(point[1]) - grid.south) / cell - 0.5;
      }
      for (let i = 0; i < n; i++) {
        const j = i + 1 === n ? 0 : i + 1;
        const ax = ringCols[i] as number;
        const ay = ringRows[i] as number;
        const bx = ringCols[j] as number;
        const by = ringRows[j] as number;
        if (ay !== by) {
          const lo = Math.max(0, Math.ceil(Math.min(ay, by)));
          const hi = Math.min(rows - 1, Math.ceil(Math.max(ay, by)) - 1);
          for (let r = lo; r <= hi; r++) crossings[r].push(ax + ((r - ay) / (by - ay)) * (bx - ax));
        }
        const dx = bx - ax;
        const dy = by - ay;
        const length2 = dx * dx + dy * dy || 1;
        const c0 = Math.max(0, Math.floor(Math.min(ax, bx)) - 1);
        const c1 = Math.min(cols - 1, Math.ceil(Math.max(ax, bx)) + 1);
        const r0 = Math.max(0, Math.floor(Math.min(ay, by)) - 1);
        const r1 = Math.min(rows - 1, Math.ceil(Math.max(ay, by)) + 1);
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const u = Math.max(0, Math.min(1, ((c - ax) * dx + (r - ay) * dy) / length2));
            const ex = c - ax - u * dx;
            const ey = r - ay - u * dy;
            const d = Math.sqrt(ex * ex + ey * ey);
            if (d < (near[r * cols + c] as number)) near[r * cols + c] = d;
          }
        }
      }
    }
    for (let r = 0; r < rows; r++) {
      const row = crossings[r].sort((p, q) => p - q);
      for (let k = 0; k + 1 < row.length; k += 2) {
        const first = Math.max(0, Math.ceil(row[k]));
        const last = Math.min(cols - 1, Math.floor(row[k + 1]));
        for (let c = first; c <= last; c++) inside[r * cols + c] = 1;
      }
    }
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const here = inside[i];
      const edge =
        (c > 0 && inside[i - 1] !== here) ||
        (c < cols - 1 && inside[i + 1] !== here) ||
        (r > 0 && inside[i - cols] !== here) ||
        (r < rows - 1 && inside[i + cols] !== here);
      if (!edge) near[i] = Number.POSITIVE_INFINITY;
    }
  }
  spread(near, cols, rows);
  for (let i = 0; i < size; i++) if (!inside[i]) near[i] = -(near[i] as number);
  return near;
}

/** Kept from one measuring to the next: which cells are inside, and a ring's points in cells. */
let insideScratch = new Uint8Array(0);
let ringCols = new Float64Array(256);
let ringRows = new Float64Array(256);

/**
 * Each cell's distance to the nearest of an origin's points and lines, in cells: measured exactly
 * near them and along the grid's edge, for the stretches beyond it, and carried on across the rest.
 */
function distancesFrom(origin: Origin, grid: BlendGrid): Float32Array {
  const { cols, rows, cell } = grid;
  const near = new Float32Array(cols * rows).fill(Number.POSITIVE_INFINITY);
  const steps: [number, number, number, number][] = [];
  for (const line of origin) {
    const at = line.map(([lon, lat]) => [
      (lon - grid.west) / cell - 0.5,
      (mercY(lat) - grid.south) / cell - 0.5,
    ]);
    const [first, ...rest] = at;
    let [x, y] = first;
    if (!rest.length) steps.push([x, y, x, y]);
    for (const [nx, ny] of rest) {
      steps.push([x, y, nx, ny]);
      [x, y] = [nx, ny];
    }
  }
  const measure = (c: number, r: number, [ax, ay, bx, by]: (typeof steps)[number]) => {
    const dx = bx - ax;
    const dy = by - ay;
    const u = Math.max(0, Math.min(1, ((c - ax) * dx + (r - ay) * dy) / (dx * dx + dy * dy || 1)));
    const d = Math.hypot(c - ax - u * dx, r - ay - u * dy);
    if (d < near[r * cols + c]) near[r * cols + c] = d;
  };
  for (const step of steps) {
    const [ax, ay, bx, by] = step;
    const c0 = Math.max(0, Math.floor(Math.min(ax, bx)) - 1);
    const c1 = Math.min(cols - 1, Math.ceil(Math.max(ax, bx)) + 1);
    const r0 = Math.max(0, Math.floor(Math.min(ay, by)) - 1);
    const r1 = Math.min(rows - 1, Math.ceil(Math.max(ay, by)) + 1);
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) measure(c, r, step);
  }
  for (const step of steps) {
    for (let c = 0; c < cols; c++) {
      measure(c, 0, step);
      measure(c, rows - 1, step);
    }
    for (let r = 1; r < rows - 1; r++) {
      measure(0, r, step);
      measure(cols - 1, r, step);
    }
  }
  spread(near, cols, rows);
  return near;
}

/** Carries distances on across the grid, a step or a diagonal at a time, both ways. */
function spread(d: Float32Array, cols: number, rows: number) {
  const D = Math.SQRT2;
  for (let r = 0; r < rows; r++) {
    const row = r * cols;
    for (let c = 0; c < cols; c++) {
      const i = row + c;
      let v = d[i] as number;
      if (c > 0) {
        const w = (d[i - 1] as number) + 1;
        if (w < v) v = w;
      }
      if (r > 0) {
        const above = i - cols;
        const w = (d[above] as number) + 1;
        if (w < v) v = w;
        if (c > 0) {
          const x = (d[above - 1] as number) + D;
          if (x < v) v = x;
        }
        if (c < cols - 1) {
          const x = (d[above + 1] as number) + D;
          if (x < v) v = x;
        }
      }
      d[i] = v;
    }
  }
  for (let r = rows - 1; r >= 0; r--) {
    const row = r * cols;
    for (let c = cols - 1; c >= 0; c--) {
      const i = row + c;
      let v = d[i] as number;
      if (c < cols - 1) {
        const w = (d[i + 1] as number) + 1;
        if (w < v) v = w;
      }
      if (r < rows - 1) {
        const below = i + cols;
        const w = (d[below] as number) + 1;
        if (w < v) v = w;
        if (c < cols - 1) {
          const x = (d[below + 1] as number) + D;
          if (x < v) v = x;
        }
        if (c > 0) {
          const x = (d[below - 1] as number) + D;
          if (x < v) v = x;
        }
      }
      d[i] = v;
    }
  }
}

/** How far a traced point may stray from the straight way between the points kept round it, in cells. */
const TRACED_TOLERANCE = 0.1;

/**
 * Kept from one tracing to the next: the two segments meeting at each edge, -1 between tracings;
 * the segments, two edges each, and which are traced yet; the squares they cross; the points of
 * the ring being traced; and each ring's box and area.
 */
let segmentAt = new Int32Array(0);
let otherSegmentAt = new Int32Array(0);
let segments = new Int32Array(1024);
let segmentCount = 0;
let segmentTraced = new Uint8Array(512);
let crossed = new Int32Array(256);
let crossedCount = 0;
let ringPoints = new Float64Array(1024);
let keptPoint = new Uint8Array(512);
let simplifying = new Int32Array(512);
let ringBoxes = new Float64Array(64);

/**
 * Keeps the points of the ring in `ringPoints`, `n` of them, that stray more than the tolerance from
 * the straight way between the points kept round them, by Douglas and Peucker, marking them in
 * `keptPoint`.
 */
function simplifyRing(n: number) {
  if (keptPoint.length < n) {
    keptPoint = new Uint8Array(n * 2);
    simplifying = new Int32Array(n * 4);
  }
  keptPoint.fill(0, 0, n);
  let far = 0;
  let farthest = -1;
  for (let i = 1; i < n; i++) {
    const d =
      (ringPoints[2 * i] - ringPoints[0]) ** 2 + (ringPoints[2 * i + 1] - ringPoints[1]) ** 2;
    if (d > farthest) {
      farthest = d;
      far = i;
    }
  }
  keptPoint[0] = 1;
  keptPoint[far] = 1;
  let top = 0;
  const push = (from: number, to: number) => {
    if (top + 2 > simplifying.length) {
      const grown = new Int32Array(simplifying.length * 2);
      grown.set(simplifying);
      simplifying = grown;
    }
    simplifying[top++] = from;
    simplifying[top++] = to;
  };
  push(0, far);
  push(far, n);
  while (top) {
    const to = simplifying[--top];
    const from = simplifying[--top];
    const end = to % n;
    const fx = ringPoints[2 * from];
    const fy = ringPoints[2 * from + 1];
    const dx = ringPoints[2 * end] - fx;
    const dy = ringPoints[2 * end + 1] - fy;
    const length2 = dx * dx + dy * dy;
    let worst = -1;
    let at = -1;
    for (let i = from + 1; i < to; i++) {
      const px = ringPoints[2 * i] - fx;
      const py = ringPoints[2 * i + 1] - fy;
      const along = length2 ? Math.max(0, Math.min(1, (px * dx + py * dy) / length2)) : 0;
      const d = (px - along * dx) ** 2 + (py - along * dy) ** 2;
      if (d > worst) {
        worst = d;
        at = i;
      }
    }
    if (at >= 0 && worst > TRACED_TOLERANCE * TRACED_TOLERANCE) {
      keptPoint[at] = 1;
      push(from, at);
      push(at, to);
    }
  }
}

/** Starts tracing on a grid, with room for its edges: 2i runs east from cell i, 2i + 1 north from it. */
function startTracing({ cols, rows }: BlendGrid) {
  const edgeCount = 2 * cols * rows;
  if (segmentAt.length < edgeCount) {
    segmentAt = new Int32Array(edgeCount).fill(-1);
    otherSegmentAt = new Int32Array(edgeCount).fill(-1);
  }
  segmentCount = 0;
  crossedCount = 0;
}

function addSegment(e0: number, e1: number) {
  if (segments.length < (segmentCount + 1) * 2) {
    const grown = new Int32Array(segments.length * 2);
    grown.set(segments);
    segments = grown;
  }
  segments[2 * segmentCount] = e0;
  segments[2 * segmentCount + 1] = e1;
  if (segmentAt[e0] < 0) segmentAt[e0] = segmentCount;
  else otherSegmentAt[e0] = segmentCount;
  if (segmentAt[e1] < 0) segmentAt[e1] = segmentCount;
  else otherSegmentAt[e1] = segmentCount;
  segmentCount++;
}

/**
 * Adds the segments the outline crosses square `i` by, each from one of its edges to another, and
 * notes the square among those crossed: the square between the centres of cell `i` and the cells
 * east, north and north-east of it.
 */
function crossSquare(values: Float32Array, i: number, cols: number) {
  const sw = values[i];
  const se = values[i + 1];
  const ne = values[i + cols + 1];
  const nw = values[i + cols];
  const corners = (sw > 0 ? 1 : 0) | (se > 0 ? 2 : 0) | (ne > 0 ? 4 : 0) | (nw > 0 ? 8 : 0);
  if (corners === 0 || corners === 15) return;
  const south = 2 * i;
  const north = 2 * (i + cols);
  const west = 2 * i + 1;
  const east = 2 * (i + 1) + 1;
  switch (corners) {
    case 1:
    case 14:
      addSegment(west, south);
      break;
    case 2:
    case 13:
      addSegment(south, east);
      break;
    case 3:
    case 12:
      addSegment(west, east);
      break;
    case 4:
    case 11:
      addSegment(east, north);
      break;
    case 6:
    case 9:
      addSegment(south, north);
      break;
    case 7:
    case 8:
      addSegment(west, north);
      break;
    case 5:
    case 10: {
      const middleInside = sw + se + ne + nw > 0;
      if ((corners === 5) === middleInside) {
        addSegment(south, east);
        addSegment(west, north);
      } else {
        addSegment(west, south);
        addSegment(east, north);
      }
    }
  }
  if (crossed.length === crossedCount) {
    const grown = new Int32Array(crossed.length * 2);
    grown.set(crossed);
    crossed = grown;
  }
  crossed[crossedCount++] = i;
}

/** Crosses every square of a grid, in order. */
function crossEverySquare(values: Float32Array, { cols, rows }: BlendGrid) {
  for (let r = 0; r < rows - 1; r++) {
    for (let c = 0; c < cols - 1; c++) crossSquare(values, r * cols + c, cols);
  }
}

/** Where a grid's values are above 0, as polygons: outlines traced between cells, then holes. */
function outlinesOf(values: Float32Array, grid: BlendGrid): Position[][][] {
  startTracing(grid);
  crossEverySquare(values, grid);
  return nested(ringsTraced(values, grid));
}

/** Squares round the cells that changed hands at the last move, to be looked at again. */
let nearFlipped = new Int32Array(1024);

/**
 * A spread's polygons `u` of the way between its keys. Only the squares the outline crossed when
 * last traced, and those round a cell that changed hands since, can be crossed now, so only they
 * are looked at again, in the order a pass over every square takes them.
 */
function spreadBetween(morph: Spread, u: number): Position[][][] {
  moveBetween(morph, u);
  const { grid, between } = morph;
  const { cols, rows } = grid;
  startTracing(grid);
  const before = morph.crossed;
  if (!before) crossEverySquare(between, grid);
  else {
    if (nearFlipped.length < flippedCount * 4) nearFlipped = new Int32Array(flippedCount * 8);
    let near = 0;
    for (let n = 0; n < flippedCount; n++) {
      const k = flipped[n];
      const c = k % cols;
      const r = (k - c) / cols;
      if (r < rows - 1 && c < cols - 1) nearFlipped[near++] = k;
      if (r < rows - 1 && c > 0) nearFlipped[near++] = k - 1;
      if (r > 0 && c < cols - 1) nearFlipped[near++] = k - cols;
      if (r > 0 && c > 0) nearFlipped[near++] = k - cols - 1;
    }
    nearFlipped.subarray(0, near).sort();
    const count = morph.crossedCount;
    let last = -1;
    for (let a = 0, b = 0; a < count || b < near; ) {
      const takeBefore = b === near || (a < count && before[a] <= nearFlipped[b]);
      const square = takeBefore ? before[a++] : nearFlipped[b++];
      if (square === last) continue;
      last = square;
      crossSquare(between, square, cols);
    }
  }
  if (!before || before.length < crossedCount) morph.crossed = new Int32Array(crossedCount * 2);
  (morph.crossed as Int32Array).set(crossed.subarray(0, crossedCount));
  morph.crossedCount = crossedCount;
  return nested(ringsTraced(between, grid));
}

/** The rings the segments make, each traced round from the first of its segments, then simplified. */
function ringsTraced(values: Float32Array, grid: BlendGrid): Position[][] {
  const { cols } = grid;
  if (segmentTraced.length < segmentCount) segmentTraced = new Uint8Array(segmentCount * 2);
  segmentTraced.fill(0, 0, segmentCount);
  const rings: Position[][] = [];
  for (let first = 0; first < segmentCount; first++) {
    if (segmentTraced[first]) continue;
    let n = 0;
    let s = first;
    let edge = segments[2 * first];
    while (s >= 0 && !segmentTraced[s]) {
      segmentTraced[s] = 1;
      if (ringPoints.length < (n + 1) * 2) {
        const grown = new Float64Array(ringPoints.length * 2);
        grown.set(ringPoints);
        ringPoints = grown;
      }
      const i = edge >> 1;
      const c = i % cols;
      const r = (i - c) / cols;
      const a = values[i];
      const u = a / (a - (edge & 1 ? values[i + cols] : values[i + 1]));
      ringPoints[2 * n] = edge & 1 ? c : c + u;
      ringPoints[2 * n + 1] = edge & 1 ? r + u : r;
      n++;
      edge = segments[2 * s] === edge ? segments[2 * s + 1] : segments[2 * s];
      const p = segmentAt[edge];
      s = p === s ? otherSegmentAt[edge] : p;
    }
    if (n < 3) continue;
    simplifyRing(n);
    const ring: Position[] = [];
    for (let k = 0; k < n; k++) {
      if (!keptPoint[k]) continue;
      ring.push([
        grid.west + (ringPoints[2 * k] + 0.5) * grid.cell,
        latOfMercY(grid.south + (ringPoints[2 * k + 1] + 0.5) * grid.cell),
      ]);
    }
    if (ring.length >= 3) rings.push(ring);
  }
  for (let e = 0; e < 2 * segmentCount; e++) {
    segmentAt[segments[e]] = -1;
    otherSegmentAt[segments[e]] = -1;
  }
  return rings;
}

/**
 * Rings as polygons: a ring inside an even number of the others is an outline, and any other a
 * hole in the smallest outline one ring further out round it. A ring's first point is looked for
 * inside another only where it falls within that ring's box.
 */
function nested(rings: Position[][]): Position[][][] {
  const count = rings.length;
  if (ringBoxes.length < count * 5) ringBoxes = new Float64Array(count * 10);
  for (let k = 0; k < count; k++) {
    const ring = rings[k];
    let west = Number.POSITIVE_INFINITY;
    let south = Number.POSITIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < ring.length; i++) {
      const x = ring[i][0];
      const y = ring[i][1];
      if (x < west) west = x;
      if (x > east) east = x;
      if (y < south) south = y;
      if (y > north) north = y;
    }
    ringBoxes[5 * k] = west;
    ringBoxes[5 * k + 1] = south;
    ringBoxes[5 * k + 2] = east;
    ringBoxes[5 * k + 3] = north;
    ringBoxes[5 * k + 4] = Math.abs(signedArea(ring));
  }
  const depth = new Int32Array(count);
  for (let k = 0; k < count; k++) {
    const x = rings[k][0][0];
    const y = rings[k][0][1];
    for (let j = 0; j < count; j++) if (j !== k && holds(rings, j, x, y)) depth[k]++;
  }
  const outlines: number[] = [];
  for (let k = 0; k < count; k++) if (depth[k] % 2 === 0) outlines.push(k);
  const polygons = outlines.map(k => [rings[k]]);
  for (let k = 0; k < count; k++) {
    if (depth[k] % 2 === 0) continue;
    const x = rings[k][0][0];
    const y = rings[k][0][1];
    let into = -1;
    let smallest = Number.POSITIVE_INFINITY;
    for (let p = 0; p < outlines.length; p++) {
      const o = outlines[p];
      const size = ringBoxes[5 * o + 4];
      if (depth[o] === depth[k] - 1 && size < smallest && holds(rings, o, x, y)) {
        smallest = size;
        into = p;
      }
    }
    if (into >= 0) polygons[into].push(rings[k]);
  }
  return polygons;
}

/**
 * Whether ring `j` holds a point, as `inRing` says. A point at or above its box's top or below its
 * bottom crosses none of its edges, and one well to either side of it crosses an even number.
 */
function holds(rings: Position[][], j: number, x: number, y: number): boolean {
  const west = ringBoxes[5 * j];
  const east = ringBoxes[5 * j + 2];
  const slack = 1e-9 * (1 + Math.abs(west) + Math.abs(east));
  if (y < ringBoxes[5 * j + 1] || y >= ringBoxes[5 * j + 3]) return false;
  if (x < west - slack || x > east + slack) return false;
  return inRing(x, y, rings[j]);
}

const alongKeys = new WeakMap<readonly Key<Position[][][]>[], Float64Array>();

/**
 * How far a shape easing `smooth` has come from key `i` toward the next at `at`, 0 to 1: along a
 * curve through every key, as a position's, so it passes through each without stopping, and comes
 * to rest only at a key the next one repeats.
 */
function smoothedAlong(keys: readonly Key<Position[][][]>[], i: number, at: number): number {
  let along = alongKeys.get(keys);
  if (!along) {
    along = new Float64Array(keys.length);
    for (let k = 1; k < keys.length; k++) {
      along[k] = along[k - 1] + (samePolygons(keys[k - 1].value, keys[k].value) ? 0 : 1);
    }
    alongKeys.set(keys, along);
  }
  const from = along[i];
  if (along[i + 1] === from) return 0;
  const h = keys[i + 1].at - keys[i].at;
  const u = h > 0 ? (at - keys[i].at) / h : 1;
  const xs = (k: number) => keys[k].at;
  const ys = (k: number) => along[k];
  const n = keys.length;
  const s = hermite(from, from + 1, slope(xs, ys, n, i), slope(xs, ys, n, i + 1), h, u);
  return Math.max(0, Math.min(1, s - from));
}

function samePolygons(a: Position[][][], b: Position[][][]): boolean {
  return (
    a === b ||
    (a.length === b.length &&
      a.every(
        (polygon, p) =>
          polygon.length === b[p].length &&
          polygon.every(
            (ring, r) =>
              ring.length === b[p][r].length &&
              ring.every(([x, y], k) => x === b[p][r][k][0] && y === b[p][r][k][1]),
          ),
      ))
  );
}

/** The pair of keys each shape last read between, by the index of the first. */
const playingPairs = new WeakMap<ShapeKeys, number>();

/** Stops keeping the morph between key `i` and the next, unless it is `still` being read. */
function letGoOfPair(keys: ShapeKeys, i: number, still: Morph) {
  const a = keys[i];
  const b = keys[i + 1];
  if (!a || !b) return;
  const origin = originOf(keys, i) ?? FROM_THE_GROUND;
  const morph = morphs[b.morph ?? "spread"].get(a.value)?.get(b.value)?.get(origin);
  if (morph && morph !== still) letGoOf(morph);
}

/** A shape's polygons at `t`, going into each key as that key's `morph` says. */
export function polygonsAt(value: ShapeGeometry, t: number): Position[][][] {
  if (Array.isArray(value)) return value;
  const at = localTime(value, t);
  const { keys } = value;
  if (at <= keys[0].at || keys.length === 1) return keys[0].value;
  if (at >= keys[keys.length - 1].at) return keys[keys.length - 1].value;
  const i = segment(keys, at);
  const a = keys[i];
  const b = keys[i + 1];
  const into = easeInto(b, value.ease ?? "linear");
  if (into === "step") return a.value;
  const u = (at - a.at) / (b.at - a.at || 1);
  const eased = into === "smooth" ? smoothedAlong(keys, i, at) : u;
  const morph = morphOf(a.value, b.value, b.morph ?? "spread", originOf(keys, i));
  const playing = playingPairs.get(keys);
  if (playing !== i) {
    if (playing !== undefined) letGoOfPair(keys, playing, morph);
    playingPairs.set(keys, i);
  }
  if (morph.kind === "holes") {
    if (morph.at !== eased) {
      const holes = spreadBetween(morph.holes, eased);
      morph.shape = [
        [morph.outline, ...holes.map(([outline]) => outline as Position[])],
        ...holes.flatMap(([, ...islands]) => islands.map(island => [island])),
      ];
      morph.at = eased;
    }
    return morph.shape;
  }
  if (morph.at !== eased) {
    morph.shape = spreadBetween(morph, eased);
    morph.at = eased;
  }
  return morph.shape;
}

type MercatorBox = [number, number, number, number];

const polygonBoxes = new WeakMap<object, MercatorBox>();
const keyBoxes = new WeakMap<object, MercatorBox>();

/** Polygons' box on the flat map: west, south, east and north, in Mercator degrees. */
function mercatorBoxOf(polygons: Position[][][]): MercatorBox {
  let box = polygonBoxes.get(polygons);
  if (!box) {
    box = [
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    ];
    for (const polygon of polygons) {
      for (const ring of polygon) {
        for (const point of ring) {
          const x = point[0];
          const y = mercY(point[1]);
          if (x < box[0]) box[0] = x;
          if (y < box[1]) box[1] = y;
          if (x > box[2]) box[2] = x;
          if (y > box[3]) box[3] = y;
        }
      }
    }
    polygonBoxes.set(polygons, box);
  }
  return box;
}

const geometryBoxes = new WeakMap<object, MercatorBox>();

/** A box on the flat map, west, south, east and north in Mercator degrees, that holds a shape at every key. */
export function shapeBox(value: ShapeGeometry): MercatorBox {
  if (Array.isArray(value)) return mercatorBoxOf(value);
  let box = geometryBoxes.get(value);
  if (!box) {
    box = [
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    ];
    for (const key of value.keys) {
      const each = mercatorBoxOf(key.value);
      if (each[0] < box[0]) box[0] = each[0];
      if (each[1] < box[1]) box[1] = each[1];
      if (each[2] > box[2]) box[2] = each[2];
      if (each[3] > box[3]) box[3] = each[3];
    }
    geometryBoxes.set(value, box);
  }
  return box;
}

/**
 * A box on the flat map, west, south, east and north in Mercator degrees, that holds a shape's
 * polygons at `t` and on either side of it until the keys around it: going from one key to the
 * next, a shape only ever covers ground one of the two covers.
 */
export function shapeBoxAt(value: ShapeGeometry, t: number): MercatorBox {
  if (Array.isArray(value)) return mercatorBoxOf(value);
  const at = localTime(value, t);
  const { keys } = value;
  if (at <= keys[0].at || keys.length === 1) return mercatorBoxOf(keys[0].value);
  if (at >= keys[keys.length - 1].at) return mercatorBoxOf(keys[keys.length - 1].value);
  const i = segment(keys, at);
  const next = keys[i + 1];
  let box = keyBoxes.get(next);
  if (!box) {
    const a = mercatorBoxOf(keys[i].value);
    const b = mercatorBoxOf(next.value);
    box = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])];
    keyBoxes.set(next, box);
  }
  return box;
}

export function lineAt(value: Animated<Position[]>, t: number): Position[] {
  if (!isTrack(value)) return value;
  const at = localTime(value, t);
  const { keys } = value;
  if (at <= keys[0].at || keys.length === 1) return keys[0].value;
  if (at >= keys[keys.length - 1].at) return keys[keys.length - 1].value;
  const i = segment(keys, at);
  const a = keys[i];
  const b = keys[i + 1];
  const u = (at - a.at) / (b.at - a.at || 1);
  if (easeInto(b, value.ease ?? "linear") === "step") return u < 1 ? a.value : b.value;
  if (u <= 0) return a.value;
  if (u >= 1) return b.value;
  const { from, to } = lineMorphOf(a.value, b.value);
  return from.map((p, k) => [
    p[0] + ((to[k] as Position)[0] - p[0]) * u,
    p[1] + ((to[k] as Position)[1] - p[1]) * u,
  ]);
}

interface LineMorph {
  from: Position[];
  to: Position[];
}

const lineMorphs = new WeakMap<Position[], WeakMap<Position[], LineMorph>>();

function lineMorphOf(a: Position[], b: Position[]): LineMorph {
  let byTo = lineMorphs.get(a);
  if (!byTo) {
    byTo = new WeakMap();
    lineMorphs.set(a, byTo);
  }
  let morph = byTo.get(b);
  if (!morph) {
    morph = matchLines(a, b);
    byTo.set(b, morph);
  }
  return morph;
}

/**
 * Two lines matched point to point, both spread evenly into points and walked in order together at
 * the least distance between partners. What both share holds still, a stretch only one has grows
 * out of, or shrinks into, the nearest point of the other, and neither folds back on itself on the
 * way. A later key drawn the other way round is matched end to end, when that is plainly nearer.
 */
function matchLines(a: Position[], b: Position[]): LineMorph {
  const count = Math.min(MOST_RESAMPLED, Math.max(RESAMPLED, a.length, b.length));
  const ra = densify(a, count);
  const rb = densify(b, count);
  const ahead = warp(ra, rb);
  const back = warp(ra, [...rb].reverse());
  const best = back.cost < ahead.cost * 0.5 ? back : ahead;
  return { from: best.from, to: best.to };
}

/* ── Lines along shapes' edges ──────────────────────────────────────── */

/** How far beyond an edge the ground is looked at to tell what the edge borders, in kilometres. */
const EDGE_REACH_KM = 1;
/** The longest piece of an edge told apart on its own, in degrees. */
const EDGE_PIECE = 0.02;
const KM_PER_DEGREE_OF_LATITUDE = 111.2;

/**
 * Polygons laid over a grid, to tell at once whether they hold a place: each cell keeps the edges
 * that cross it and which polygons hold its centre, so a place is decided by the few edges between
 * it and the centre of its cell.
 */
export interface Coverage {
  /** Whether any of the polygons holds the place, each by the even-odd rule. */
  holds(x: number, y: number): boolean;
}

const coverages = new WeakMap<Position[][][], Coverage>();

/** The polygons' coverage, worked out once for as long as the polygons are. */
export function coverageOf(polygons: Position[][][]): Coverage {
  let known = coverages.get(polygons);
  if (!known) {
    known = coveringGrid(polygons);
    coverages.set(polygons, known);
  }
  return known;
}

/** Whether a straight walk from `p` to `q` crosses the edge from `a` to `b`, counting a shared corner once. */
function crosses(
  px: number,
  py: number,
  qx: number,
  qy: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): boolean {
  const toA = (qx - px) * (ay - py) - (qy - py) * (ax - px) > 0;
  const toB = (qx - px) * (by - py) - (qy - py) * (bx - px) > 0;
  if (toA === toB) return false;
  const toP = (bx - ax) * (py - ay) - (by - ay) * (px - ax) > 0;
  const toQ = (bx - ax) * (qy - ay) - (by - ay) * (qx - ax) > 0;
  return toP !== toQ;
}

function coveringGrid(polygons: Position[][][]): Coverage {
  let count = 0;
  for (const polygon of polygons)
    for (const ring of polygon) if (ring.length >= 3) count += ring.length;
  if (!count) return { holds: () => false };
  const ax = new Float64Array(count);
  const ay = new Float64Array(count);
  const bx = new Float64Array(count);
  const by = new Float64Array(count);
  const owner = new Int32Array(count);
  let [west, south, east, north] = [Infinity, Infinity, -Infinity, -Infinity];
  let edges = 0;
  polygons.forEach((polygon, p) => {
    for (const ring of polygon) {
      if (ring.length < 3) continue;
      for (let i = 0; i < ring.length; i++) {
        const [x0, y0] = ring[i] as Position;
        const [x1, y1] = ring[(i + 1) % ring.length] as Position;
        ax[edges] = x0;
        ay[edges] = y0;
        bx[edges] = x1;
        by[edges] = y1;
        owner[edges] = p;
        edges++;
        if (x0 < west) west = x0;
        if (x0 > east) east = x0;
        if (y0 < south) south = y0;
        if (y0 > north) north = y0;
      }
    }
  });
  const width = Math.max(east - west, 1e-9);
  const height = Math.max(north - south, 1e-9);
  const wanted = Math.min(1 << 20, Math.max(16, edges * 2));
  const cols = Math.min(wanted, Math.max(1, Math.round(Math.sqrt((wanted * width) / height))));
  const rows = Math.min(wanted, Math.max(1, Math.round(wanted / cols)));
  const cw = width / cols;
  const ch = height / rows;
  const colOf = (x: number) => Math.min(cols - 1, Math.max(0, Math.floor((x - west) / cw)));
  const rowOf = (y: number) => Math.min(rows - 1, Math.max(0, Math.floor((y - south) / ch)));

  const cells = cols * rows;
  const starts = new Int32Array(cells + 1);
  const along = (k: number) =>
    cellsAlong(
      (ax[k] - west) / cw,
      (ay[k] - south) / ch,
      (bx[k] - west) / cw,
      (by[k] - south) / ch,
      cols,
      rows,
    );
  for (let k = 0; k < edges; k++) {
    const n = along(k);
    for (let i = 0; i < n; i++) starts[walked[i] + 1]++;
  }
  for (let i = 0; i < cells; i++) starts[i + 1] += starts[i];
  const entries = new Int32Array(starts[cells]);
  const filled = starts.slice(0, cells);
  for (let k = 0; k < edges; k++) {
    const n = along(k);
    for (let i = 0; i < n; i++) entries[filled[walked[i]]++] = k;
  }

  // Which polygons hold each cell's centre, swept along each row of centres from the west.
  const rowStarts = new Int32Array(rows + 1);
  const rowSpan = (k: number): [number, number] => {
    const low = Math.min(ay[k], by[k]);
    const high = Math.max(ay[k], by[k]);
    return [
      Math.max(0, Math.ceil((low - south) / ch - 0.5)),
      Math.min(rows - 1, Math.ceil((high - south) / ch - 0.5) - 1),
    ];
  };
  for (let k = 0; k < edges; k++) {
    const [first, last] = rowSpan(k);
    for (let r = first; r <= last; r++) rowStarts[r + 1]++;
  }
  for (let r = 0; r < rows; r++) rowStarts[r + 1] += rowStarts[r];
  const rowEdges = new Int32Array(rowStarts[rows]);
  const rowFilled = rowStarts.slice(0, rows);
  for (let k = 0; k < edges; k++) {
    const [first, last] = rowSpan(k);
    for (let r = first; r <= last; r++) rowEdges[rowFilled[r]++] = k;
  }
  const centreHeld = new Uint8Array(cells);
  const heldElsewhere = new Uint8Array(cells);
  const entryParity = new Uint8Array(entries.length);
  const parity = new Uint8Array(polygons.length);
  const seen = new Int32Array(polygons.length).fill(-1);
  let crossings = new Float64Array(64);
  let order = new Int32Array(64);
  for (let r = 0; r < rows; r++) {
    const y = south + (r + 0.5) * ch;
    const first = rowStarts[r];
    const n = rowStarts[r + 1] - first;
    if (crossings.length < n) {
      crossings = new Float64Array(n * 2);
      order = new Int32Array(n * 2);
    }
    for (let i = 0; i < n; i++) {
      const k = rowEdges[first + i];
      crossings[i] = ax[k] + ((y - ay[k]) * (bx[k] - ax[k])) / (by[k] - ay[k]);
      order[i] = i;
    }
    const sorted = order.subarray(0, n).sort((i, j) => crossings[i] - crossings[j]);
    let next = 0;
    let odd = 0;
    for (let c = 0; c < cols; c++) {
      const x = west + (c + 0.5) * cw;
      while (next < n && crossings[sorted[next]] < x) {
        const p = owner[rowEdges[first + sorted[next]]];
        parity[p] ^= 1;
        odd += parity[p] ? 1 : -1;
        next++;
      }
      const cell = r * cols + c;
      centreHeld[cell] = odd > 0 ? 1 : 0;
      let listedOdd = 0;
      for (let k = starts[cell]; k < starts[cell + 1]; k++) {
        const p = owner[entries[k]];
        entryParity[k] = parity[p];
        if (seen[p] !== cell) {
          seen[p] = cell;
          listedOdd += parity[p];
        }
      }
      heldElsewhere[cell] = odd - listedOdd > 0 ? 1 : 0;
    }
    parity.fill(0);
  }

  const stamp = new Int32Array(polygons.length);
  const touched = new Int32Array(polygons.length);
  let query = 0;
  return {
    holds(x, y) {
      if (!(x >= west && x <= east && y >= south && y <= north)) return false;
      const c = colOf(x);
      const r = rowOf(y);
      const cell = r * cols + c;
      const start = starts[cell];
      const end = starts[cell + 1];
      if (start === end) return centreHeld[cell] === 1;
      if (heldElsewhere[cell]) return true;
      const qx = west + (c + 0.5) * cw;
      const qy = south + (r + 0.5) * ch;
      query++;
      let count = 0;
      for (let k = start; k < end; k++) {
        const edge = entries[k];
        const p = owner[edge];
        if (stamp[p] !== query) {
          stamp[p] = query;
          parity[p] = entryParity[k];
          touched[count++] = p;
        }
        if (crosses(x, y, qx, qy, ax[edge], ay[edge], bx[edge], by[edge])) parity[p] ^= 1;
      }
      let held = false;
      for (let i = 0; i < count; i++) {
        const p = touched[i];
        if (parity[p]) held = true;
        parity[p] = 0;
      }
      return held;
    },
  };
}

/** The cells a walk across the grid passes through, left in `walked`, by Amanatides and Woo. */
let walked = new Int32Array(256);

/** How many cells the edge from `x0, y0` to `x1, y1`, in cells, passes through, each once. */
function cellsAlong(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  cols: number,
  rows: number,
): number {
  let c = Math.floor(x0);
  let r = Math.floor(y0);
  const steps = Math.abs(Math.floor(x1) - c) + Math.abs(Math.floor(y1) - r);
  if (walked.length < steps + 1) walked = new Int32Array((steps + 1) * 2);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const stepC = dx > 0 ? 1 : -1;
  const stepR = dy > 0 ? 1 : -1;
  const deltaC = dx ? Math.abs(1 / dx) : Infinity;
  const deltaR = dy ? Math.abs(1 / dy) : Infinity;
  let nextC = dx > 0 ? (c + 1 - x0) / dx : dx < 0 ? (x0 - c) / -dx : Infinity;
  let nextR = dy > 0 ? (r + 1 - y0) / dy : dy < 0 ? (y0 - r) / -dy : Infinity;
  let n = 0;
  let last = -1;
  for (let step = 0; ; step++) {
    const cell = Math.min(rows - 1, Math.max(0, r)) * cols + Math.min(cols - 1, Math.max(0, c));
    if (cell !== last) walked[n++] = cell;
    last = cell;
    if (step === steps) return n;
    if (nextC < nextR) {
      c += stepC;
      nextC += deltaC;
    } else {
      r += stepR;
      nextR += deltaR;
    }
  }
}

function lengthOf(line: Position[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i++) {
    const shrink = Math.cos((line[i][1] * Math.PI) / 180);
    total += Math.hypot((line[i][0] - line[i - 1][0]) * shrink, line[i][1] - line[i - 1][1]);
  }
  return total;
}

/**
 * The outline of the polygons `of`, each its outline and then its holes, as stretches that run
 * with that ground on their left, longest first. An edge two of the polygons share is left out,
 * and with `against`, so is every piece of edge whose ground a kilometre beyond lies outside it.
 */
export function edgeStretches(
  of: Position[][][],
  against?: Position[][][] | Coverage,
): Position[][] {
  const ofGround = coveringGrid(of);
  const againstGround = against && (Array.isArray(against) ? coverageOf(against) : against);
  const reach = EDGE_REACH_KM / KM_PER_DEGREE_OF_LATITUDE;
  /** Whether the piece from `a` to `b` borders what the line runs along, by the ground just beyond it. */
  const borders = (ax: number, ay: number, bx: number, by: number): boolean => {
    const lat = (ay + by) / 2;
    const shrink = Math.max(0.05, Math.cos((lat * Math.PI) / 180));
    const dx = (bx - ax) * shrink;
    const dy = by - ay;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return false;
    const x = (ax + bx) / 2 + ((dy / length) * reach) / shrink;
    const y = lat - (dx / length) * reach;
    if (ofGround.holds(x, y)) return false;
    return !againstGround || againstGround.holds(x, y);
  };
  const stretches: Position[][] = [];
  for (const polygon of of) {
    polygon.forEach((ring, r) => {
      if (ring.length < 3) return;
      const n = ring.length;
      const forward = signedArea(ring) > 0 === (r === 0);
      const own: Position[][] = [];
      let current: Position[] = [];
      let firstKept = false;
      for (let i = 0; i < n; i++) {
        const [ax, ay] = ring[forward ? i : n - 1 - i] as Position;
        const b = ring[forward ? (i + 1) % n : (2 * n - 2 - i) % n] as Position;
        const span = Math.sqrt((b[0] - ax) ** 2 + (b[1] - ay) ** 2);
        const pieces = Math.max(1, Math.ceil(span / EDGE_PIECE));
        const stepX = (b[0] - ax) / pieces;
        const stepY = (b[1] - ay) / pieces;
        for (let j = 0; j < pieces; j++) {
          const sx = ax + stepX * j;
          const sy = ay + stepY * j;
          const last = j + 1 === pieces;
          const ex = last ? b[0] : ax + stepX * (j + 1);
          const ey = last ? b[1] : ay + stepY * (j + 1);
          const kept = borders(sx, sy, ex, ey);
          if (i === 0 && j === 0) firstKept = kept;
          if (kept) {
            if (!current.length) current.push([sx, sy]);
            current.push(last ? b : [ex, ey]);
          } else if (current.length) {
            own.push(current);
            current = [];
          }
        }
      }
      if (current.length) {
        if (firstKept && own.length) own[0] = [...current, ...(own[0] as Position[]).slice(1)];
        else own.push(current);
      }
      stretches.push(...own);
    });
  }
  return joined(stretches)
    .map(line => ({ line, length: lengthOf(line) }))
    .sort((p, q) => q.length - p.length)
    .map(({ line }) => line);
}

/** Stretches joined wherever one ends where another starts, so ground followed in pieces runs on unbroken. */
function joined(stretches: Position[][]): Position[][] {
  const key = ([x, y]: Position) => `${Math.round(x * 1e9)},${Math.round(y * 1e9)}`;
  const starting = new Map<string, number[]>();
  const ending = new Map<string, number[]>();
  stretches.forEach((line, k) => {
    const s = key(line[0] as Position);
    const e = key(line[line.length - 1] as Position);
    starting.set(s, [...(starting.get(s) ?? []), k]);
    ending.set(e, [...(ending.get(e) ?? []), k]);
  });
  const used = new Uint8Array(stretches.length);
  const take = (from: Map<string, number[]>, at: string) => {
    const k = from.get(at)?.find(each => !used[each]);
    if (k === undefined) return undefined;
    used[k] = 1;
    return stretches[k] as Position[];
  };
  const out: Position[][] = [];
  for (let k = stretches.length - 1; k >= 0; k--) {
    if (used[k]) continue;
    used[k] = 1;
    const line = [...(stretches[k] as Position[])];
    for (;;) {
      const first = key(line[0] as Position);
      const end = key(line[line.length - 1] as Position);
      if (first === end) break;
      const after = take(starting, end);
      if (after) {
        line.push(...after.slice(1));
        continue;
      }
      const before = take(ending, first);
      if (!before) break;
      line.unshift(...before.slice(0, -1));
    }
    out.push(line);
  }
  return out;
}

/** A screen's size in device pixels: what an extent is fitted to, and a fly sees across. */
export interface Screen {
  width: number;
  height: number;
}

/** A laptop's screen, for a camera read without one. */
const LAPTOP: Screen = { width: 2000, height: 1250 };

/** Where a screen looks on a map: what is at its middle, and how far in. */
export interface View {
  canvas: string;
  center: Position;
  zoom: number;
}

/** A board on screen, all of it. */
export interface BoardView {
  canvas: string;
}

/** Whether the camera looks at a map from a centre and a zoom, rather than at a board. */
export const isMapView = (view: View | BoardView): view is View => "center" in view;

/**
 * A camera as one screen plays it, its shots in one run of keys: each key on a map at the view its
 * extent takes there, how the camera moves to it on one canvas, and, the first of a shot, how the
 * shot comes in where the canvas changes.
 */
export interface FittedCamera {
  keys: { at: number; value: View | BoardView; move?: CameraMove; switch?: CameraSwitch }[];
}

/** How much more than an extent a screen fitting it shows, so its edges keep some room. */
const FIT_ROOM = 1.2;
/** The farthest in an extent is fitted. */
const FIT_DEEPEST = 16;
/** How far from the equator a view's middle may lie, as the map's own latitudes do. */
const FIT_LATITUDE = 85;

/**
 * The view an extent takes on `screen`: centred on it, and as far in as shows all of it with some
 * room round it, however wide or tall the screen. An extent whose east edge lies west of its west
 * edge crosses the antimeridian.
 */
export function viewOf(extent: Extent, screen: Screen): View {
  const [west, south] = extent.southwest;
  const [east, north] = extent.northeast;
  const across = Math.max(east >= west ? east - west : east + 360 - west, 1e-6);
  const clamp = (lat: number) => Math.min(FIT_LATITUDE, Math.max(-FIT_LATITUDE, lat));
  const top = mercY(clamp(north));
  const bottom = mercY(clamp(south));
  const down = Math.max(Math.abs(top - bottom), 1e-6);
  const fit = Math.log2(
    (Math.min(screen.width / across, screen.height / down) * 360) / 512 / FIT_ROOM,
  );
  const middle = west + across / 2;
  return {
    canvas: extent.canvasId,
    center: [middle > 180 ? middle - 360 : middle, latOfMercY((top + bottom) / 2)],
    zoom: Math.min(FIT_DEEPEST, Math.max(0, fit)),
  };
}

/**
 * The extent `view` shows on `screen`, less the room `viewOf` keeps round an extent, so `viewOf`
 * gives the view back on that screen: what a view by its middle and zoom is as an extent.
 */
export function extentOf(view: View, screen: Screen = LAPTOP): Extent {
  const degrees = (pixels: number) => (pixels * 360) / 512 / 2 ** view.zoom / FIT_ROOM;
  const [lon, lat] = view.center;
  const across = Math.min(degrees(screen.width), 360);
  const middle = mercY(Math.min(FIT_LATITUDE, Math.max(-FIT_LATITUDE, lat)));
  const down = degrees(screen.height);
  const kept = (value: number) => Math.round(value * 1e6) / 1e6;
  const edge = (value: number) =>
    kept(value > 180 ? value - 360 : value < -180 ? value + 360 : value);
  return {
    canvasId: view.canvas,
    southwest: [
      edge(lon - across / 2),
      kept(Math.max(-FIT_LATITUDE, latOfMercY(middle - down / 2))),
    ],
    northeast: [
      edge(lon + across / 2),
      kept(Math.min(FIT_LATITUDE, latOfMercY(middle + down / 2))),
    ],
  };
}

/** The canvas a camera's shot is on. */
export const shotCanvas = (shot: Shot): string => ("map" in shot ? shot.map : shot.board);

/** A camera as `screen` plays it: each key on a map at the view its extent takes there. */
export function fitCamera(camera: Camera, screen: Screen = LAPTOP): FittedCamera {
  return {
    keys: camera.shots.flatMap(shot => {
      const coming = shot.in ? { switch: shot.in } : {};
      if ("board" in shot) return [{ at: shot.at, value: { canvas: shot.board }, ...coming }];
      return shot.keys.map(({ at, extent, ease }, index) => ({
        at,
        value: viewOf({ canvasId: shot.map, ...extent }, screen),
        move: ease ?? shot.ease ?? "pan",
        ...(index === 0 ? coming : {}),
      }));
    }),
  };
}

/** How far a fly goes out for the distance it covers: van Wijk and Nuij's ρ, as Mapbox's flyTo has it. */
const FLY_CURVE = 1.42;

/**
 * Where a fitted camera looks at `t` on `screen`. Between two keys on one canvas it moves as the
 * later key's move says, `pan` when it gives none: `step` holds and cuts at the later key, `linear`
 * moves evenly, `pan` eases out of one key and into the next, `fly` goes out as far as the move
 * needs on that screen and back in, and `smooth` eases through the run of keys. A key on another
 * canvas is reached at its `at`, and a key on a board holds until the next; a crossfade or slide
 * from a board to a map is on the map throughout, for the board to leave over it.
 */
export function cameraAt(
  track: FittedCamera,
  t: number,
  screen: Screen = LAPTOP,
): View | BoardView {
  const { keys } = track;
  const first = keys[0] as FittedCamera["keys"][number];
  const last = keys[keys.length - 1] as FittedCamera["keys"][number];
  if (t <= first.at) return first.value;
  if (t >= last.at) return last.value;
  const i = segment(keys, t);
  const from = keys[i] as FittedCamera["keys"][number];
  const to = keys[i + 1] as FittedCamera["keys"][number];
  const { canvas } = from.value;
  if (to.value.canvas !== canvas || !isMapView(from.value) || !isMapView(to.value)) {
    const crossing =
      to.value.canvas !== canvas && (to.switch === "crossfade" || to.switch === "slide");
    return crossing && !isMapView(from.value) && isMapView(to.value) ? to.value : from.value;
  }
  const ease = to.move ?? "pan";
  if (ease === "step") return from.value;
  if (ease === "smooth") {
    let start = i;
    let end = i + 1;
    while (start > 0 && keys[start - 1]?.value.canvas === canvas) start--;
    while (end < keys.length - 1 && keys[end + 1]?.value.canvas === canvas) end++;
    const run = keys
      .slice(start, end + 1)
      .flatMap(key => (isMapView(key.value) ? [{ at: key.at, value: key.value }] : []));
    sampleComponents(
      run.map(key => ({ at: key.at, value: key.value.center })),
      "smooth",
      t,
      2,
      (v, a) => (v as Position)[a],
      scratch,
    );
    return {
      canvas,
      center: [scratch[0], scratch[1]],
      zoom: numberAt(
        { keys: run.map(key => ({ at: key.at, value: key.value.zoom })), ease: "smooth" },
        t,
        from.value.zoom,
      ),
    };
  }
  const u = (t - from.at) / (to.at - from.at);
  const s = ease === "linear" ? u : inAndOut(u);
  if (ease === "fly") return { canvas, ...flyAt(from.value, to.value, s, screen) };
  const [lon0, lat0] = from.value.center;
  const [lon1, lat1] = to.value.center;
  const y0 = mercY(lat0);
  const y1 = mercY(lat1);
  return {
    canvas,
    center: [lon0 + (lon1 - lon0) * s, latOfMercY(y0 + (y1 - y0) * s)],
    zoom: from.value.zoom + (to.value.zoom - from.value.zoom) * s,
  };
}

/** A crossfade or slide from one canvas to the next under way at a moment, `k` of its way along. */
export interface Crossing {
  ease: "crossfade" | "slide";
  from: View | BoardView;
  to: View | BoardView;
  k: number;
}

/** The crossfade or slide to another canvas under way at `t`, if one is. */
export function crossingAt(track: FittedCamera, t: number): Crossing | undefined {
  const { keys } = track;
  const i = segment(keys, t);
  const from = keys[i];
  const to = keys[i + 1];
  if (!from || !to || t < from.at || t >= to.at || to.value.canvas === from.value.canvas) return;
  const ease = to.switch;
  if (ease !== "crossfade" && ease !== "slide") return;
  return { ease, from: from.value, to: to.value, k: inAndOut((t - from.at) / (to.at - from.at)) };
}

/**
 * A fly from one view to the next, `k` of its way along: van Wijk and Nuij's smooth zooming and
 * panning, as Mapbox's flyTo does it. It goes out only as far as seeing from one end to the other
 * across `screen` needs, and covers the distance fastest while out.
 */
function flyAt(from: View, to: View, k: number, screen: Screen): Omit<View, "canvas"> {
  const x0 = from.center[0];
  const y0 = mercY(from.center[1]);
  const dx = to.center[0] - x0;
  const dy = mercY(to.center[1]) - y0;
  const w0 = Math.max(screen.width, screen.height);
  const w1 = w0 / 2 ** (to.zoom - from.zoom);
  const u1 = (Math.hypot(dx, dy) * 512 * 2 ** from.zoom) / 360;
  const path = flyPath(w0, w1, u1, FLY_CURVE);
  const s = k * path.length;
  const along = path.along(s);
  return {
    center: [x0 + dx * along, latOfMercY(y0 + dy * along)],
    zoom: from.zoom - Math.log2(path.width(s)),
  };
}

/**
 * van Wijk and Nuij's path between two views `u1` device pixels apart at the first's zoom, seeing
 * `w0` across at the start and `w1` at the end: at `s` of its `length`, the width it sees against
 * `w0` and the share of the distance covered.
 */
function flyPath(w0: number, w1: number, u1: number, rho: number) {
  const rho2 = rho * rho;
  const r = (descent: boolean) =>
    -Math.asinh(
      (w1 * w1 - w0 * w0 + (descent ? -1 : 1) * rho2 * rho2 * u1 * u1) /
        (2 * (descent ? w1 : w0) * rho2 * u1),
    );
  const r0 = r(false);
  const length = (r(true) - r0) / rho;
  if (u1 < 1e-6 || !Number.isFinite(length)) {
    const sign = w1 < w0 ? -1 : 1;
    return {
      length: Math.abs(Math.log(w1 / w0)) / rho,
      width: (s: number) => Math.exp(sign * rho * s),
      along: () => 0,
    };
  }
  return {
    length,
    width: (s: number) => Math.cosh(r0) / Math.cosh(r0 + rho * s),
    along: (s: number) =>
      (w0 * (Math.cosh(r0) * Math.tanh(r0 + rho * s) - Math.sinh(r0))) / rho2 / u1,
  };
}

/* ── Grids ──────────────────────────────────────────────────────────── */

/**
 * Cells square on the map, counted row by row from the north-west: `north` is a latitude, and
 * `cell` a size in degrees of Mercator. With an `area`, only the cells whose centres its polygons
 * cover exist.
 */
export interface Grid {
  west: number;
  north: number;
  cell: number;
  cols: number;
  rows: number;
  area?: Position[][][];
}

/**
 * Which cells of a grid exist: 1 where its area covers the cell's centre. A polygon covers a point
 * an odd number of its rings surround; the area covers it where any of its polygons does.
 */
export function cellsOf(grid: Grid): Uint8Array {
  const { cols, rows, area } = grid;
  const out = new Uint8Array(cols * rows);
  if (!area) return out.fill(1);
  const top = mercY(grid.north);
  const rowOf = (lat: number) => (top - mercY(lat)) / grid.cell - 0.5;
  const colOf = (lon: number) => (lon - grid.west) / grid.cell - 0.5;
  const crossings = new Map<number, number[]>();
  for (const polygon of area) {
    crossings.clear();
    for (const ring of polygon) {
      for (let i = 0; i < ring.length; i++) {
        const [ax, aLat] = ring[i];
        const [bx, bLat] = ring[(i + 1) % ring.length];
        const ay = rowOf(aLat);
        const by = rowOf(bLat);
        if (ay === by) continue;
        const lo = Math.max(0, Math.ceil(Math.min(ay, by)));
        const hi = Math.min(rows - 1, Math.ceil(Math.max(ay, by)) - 1);
        for (let r = lo; r <= hi; r++) {
          const x = colOf(ax + ((r - ay) / (by - ay)) * (bx - ax));
          const row = crossings.get(r);
          if (row) row.push(x);
          else crossings.set(r, [x]);
        }
      }
    }
    for (const [r, xs] of crossings) {
      xs.sort((a, b) => a - b);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const from = Math.max(0, Math.ceil(xs[k]));
        const to = Math.min(cols - 1, Math.floor(xs[k + 1]));
        for (let c = from; c <= to; c++) out[r * cols + c] = 1;
      }
    }
  }
  return out;
}

/** How far each cell is from the nearest cell marked, in cells, a step or a diagonal at a time. */
export function distanceTo(marked: ArrayLike<number>, cols: number, rows: number): Float32Array {
  const d = new Float32Array(cols * rows);
  for (let i = 0; i < d.length; i++) d[i] = marked[i] ? 0 : 1e6;
  const D = Math.SQRT2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      let v = d[i];
      if (c > 0) v = Math.min(v, d[i - 1] + 1);
      if (r > 0) {
        v = Math.min(v, d[i - cols] + 1);
        if (c > 0) v = Math.min(v, d[i - cols - 1] + D);
        if (c < cols - 1) v = Math.min(v, d[i - cols + 1] + D);
      }
      d[i] = v;
    }
  }
  for (let r = rows - 1; r >= 0; r--) {
    for (let c = cols - 1; c >= 0; c--) {
      const i = r * cols + c;
      let v = d[i];
      if (c < cols - 1) v = Math.min(v, d[i + 1] + 1);
      if (r < rows - 1) {
        v = Math.min(v, d[i + cols] + 1);
        if (c < cols - 1) v = Math.min(v, d[i + cols + 1] + D);
        if (c > 0) v = Math.min(v, d[i + cols - 1] + D);
      }
      d[i] = v;
    }
  }
  return d;
}

/** How far inside the cells marked each cell lies, in cells, below 0 outside them. */
export function signedDistance(
  inside: ArrayLike<number>,
  cols: number,
  rows: number,
): Float32Array {
  const outside = new Uint8Array(cols * rows);
  for (let i = 0; i < outside.length; i++) outside[i] = inside[i] ? 0 : 1;
  const toInside = distanceTo(inside, cols, rows);
  const toOutside = distanceTo(outside, cols, rows);
  const d = new Float32Array(outside.length);
  for (let i = 0; i < d.length; i++) d[i] = inside[i] ? toOutside[i] - 0.5 : 0.5 - toInside[i];
  return d;
}

/**
 * Where values on a grid, row by row from the north-west, are above `level`: as polygons, the
 * area a contour at `level` closes round. Beyond the grid's edge counts as below.
 */
export function areaAbove(grid: Grid, values: ArrayLike<number>, level = 0): Position[][][] {
  const cols = grid.cols + 2;
  const rows = grid.rows + 2;
  const upward = new Float32Array(cols * rows).fill(-1);
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      upward[(grid.rows - r) * cols + c + 1] = values[r * grid.cols + c] - level || -1e-6;
    }
  }
  const south = mercY(grid.north) - (grid.rows + 1) * grid.cell;
  return outlinesOf(upward, { west: grid.west - grid.cell, south, cell: grid.cell, cols, rows });
}
